import { createAsyncThunk, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { CmtsReduxThunkExtra, CmtsSerializableError } from "./thunkExtra.js";
import { serializeReduxError, serializeRepositoryFailure } from "./thunkExtra.js";

export type CmtsDashboardFilters = {
  readonly tenantCode?: string | undefined;
  readonly siteId?: string | undefined;
  readonly departmentId?: string | undefined;
  readonly status?: string | undefined;
  readonly fromDate?: string | undefined;
  readonly toDate?: string | undefined;
};

export type CmtsDashboardState = {
  readonly filters: CmtsDashboardFilters;
  readonly selectedKpi?: string | undefined;
  readonly drilldownPath?: string | undefined;
  readonly loading: boolean;
  readonly error?: CmtsSerializableError | undefined;
  readonly lastRefreshAtUtc?: string | undefined;
};


export type FetchCmtsDashboardSummaryArgs = { readonly dashboardType?: "management" | "department" | "personal" | "customer" | "supplier" | undefined; readonly query?: Record<string, string | number | boolean | undefined> | undefined };

export const fetchCmtsDashboardSummary = createAsyncThunk<{ readonly generatedAtUtc?: string | undefined }, FetchCmtsDashboardSummaryArgs | undefined, { extra: CmtsReduxThunkExtra; rejectValue: CmtsSerializableError }>(
  "dashboard/fetchSummary",
  async (args, thunkApi) => {
    try {
      if (!thunkApi.extra.repositories) {
        return thunkApi.rejectWithValue({ message: "Repository suite is not available for dashboard summary refresh.", atUtc: new Date().toISOString() });
      }
      const result = await thunkApi.extra.repositories.dashboards.get(args?.dashboardType ?? "management", args?.query ?? {});
      if (!result.ok) {
        return thunkApi.rejectWithValue(serializeRepositoryFailure(result.diagnostics, "Dashboard summary refresh failed."));
      }
      return { generatedAtUtc: result.data.generatedAtUtc };
    } catch (error: unknown) {
      return thunkApi.rejectWithValue(serializeReduxError(error, "Dashboard summary refresh failed."));
    }
  },
);

const initialState: CmtsDashboardState = {
  filters: {},
  loading: false,
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    dashboardFiltersChanged(state, action: PayloadAction<CmtsDashboardFilters>) {
      state.filters = { ...state.filters, ...action.payload };
    },
    dashboardKpiSelected(state, action: PayloadAction<{ kpi?: string | undefined; drilldownPath?: string | undefined }>) {
      state.selectedKpi = action.payload.kpi;
      state.drilldownPath = action.payload.drilldownPath;
    },
    dashboardLoadStarted(state) {
      state.loading = true;
      state.error = undefined;
    },
    dashboardLoadCompleted(state) {
      state.loading = false;
      state.lastRefreshAtUtc = new Date().toISOString();
    },
    dashboardErrorRecorded(state, action: PayloadAction<CmtsSerializableError>) {
      state.loading = false;
      state.error = action.payload;
    },
    dashboardCleared(state) {
      state.filters = {};
      state.selectedKpi = undefined;
      state.drilldownPath = undefined;
      state.loading = false;
      state.error = undefined;
      state.lastRefreshAtUtc = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCmtsDashboardSummary.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchCmtsDashboardSummary.fulfilled, (state, action) => {
        state.loading = false;
        state.lastRefreshAtUtc = action.payload.generatedAtUtc ?? new Date().toISOString();
      })
      .addCase(fetchCmtsDashboardSummary.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? serializeReduxError(action.error, "Dashboard summary refresh failed.");
      });
  },
});

export const dashboardActions = dashboardSlice.actions;
export const dashboardReducer = dashboardSlice.reducer;
